import { formatLocaleDate, formatPhoneNumber } from "@/utils/formatUtils";
import { cssVariables, isOtherContact } from "@/utils/utils";
import { Divider } from "antd";
import { useTranslation } from "react-i18next";
import { ContactType, MarketingConsent } from "../../enums";
import { Contact } from "../../types";
import AntIcon from "../icons/AntIcon";
import CopyToClipboardView from "../views/CopyToClipboardView";
import Ellipsis from "../views/Ellipsis";
import styles from "./ContactsView.module.scss";
import { ContactTypeIcon } from "./ContactTypeIcon";

interface Props {
  contacts: Contact[];
  marketingConsentsAreApplicable?: boolean;
  size?: "normal" | "small";
  showMainContactsDivider?: boolean;
}

export const ContactsView = ({
  contacts,
  marketingConsentsAreApplicable,
  size = "normal",
  showMainContactsDivider,
}: Props) => {
  const { t } = useTranslation();

  const resolveContactValue = (contact: Contact): string | undefined => {
    switch (contact.type) {
      case ContactType.PHONE_NUMBER:
      case ContactType.MAIN_PHONE_NUMBER:
        return formatPhoneNumber(contact.value);
      default:
        return contact.value;
    }
  };

  const resolveContactAnchor = (contact: Contact): React.ReactNode => {
    switch (contact.type) {
      case ContactType.EMAIL:
      case ContactType.MAIN_EMAIL:
        return <a href={`mailto:${contact.value}`}>{contact.value}</a>;
      case ContactType.PHONE_NUMBER:
      case ContactType.MAIN_PHONE_NUMBER:
        return <a href={`tel:${contact.value}`}>{formatPhoneNumber(contact.value)}</a>;
      default:
        return contact.value;
    }
  };

  const mainContacts = contacts.filter(
    (contact) => contact.type === ContactType.MAIN_EMAIL || contact.type === ContactType.MAIN_PHONE_NUMBER,
  );

  const notMainContacts = contacts.filter(
    (contact) => contact.type !== ContactType.MAIN_EMAIL && contact.type !== ContactType.MAIN_PHONE_NUMBER,
  );

  return contacts.length ? (
    <table className={styles.contactsTableView}>
      <tbody>
        {marketingConsentsAreApplicable && (
          <tr>
            <td colSpan={3} className="sub-header-info">
              {t("contact.helpers.contactDataLabel")}
            </td>
            <td className="sub-header-info">{t("contact.helpers.marketingConsentDataLabel")}</td>
          </tr>
        )}

        {mainContacts.length > 0 && (
          <>
            {mainContacts.map((contact) => (
              <tr key={`${contact.type}-${contact.value}`}>
                <td className={styles.type}>
                  <span className={styles.typeContent}>
                    <ContactTypeIcon showTooltip type={contact.type} />
                  </span>
                </td>
                <td className={styles.value}>
                  <CopyToClipboardView
                    content={resolveContactValue(contact)}
                    wrapper={
                      <Ellipsis
                        tooltip={resolveContactValue(contact)}
                        className={size === "small" ? styles.valueContentSmall : styles.valueContent}
                      >
                        {resolveContactAnchor(contact)}
                      </Ellipsis>
                    }
                  />
                </td>
                <td className={styles.description}>
                  <Ellipsis className={size === "small" ? styles.descriptionContentSmall : styles.descriptionContent}>
                    {contact.description}
                  </Ellipsis>
                </td>
                {marketingConsentsAreApplicable && !isOtherContact(contact) && (
                  <td className={styles.marketingConsent}>
                    {contact.marketingConsent === MarketingConsent.GRANTED ? (
                      <span className={styles.marketingConsentContent}>
                        <AntIcon type="check-circle-two-tone" twoToneColor={cssVariables.colorGreen} />
                        <Divider orientation="vertical" />
                        {contact.marketingConsentStartDate
                          ? formatLocaleDate(contact.marketingConsentStartDate)
                          : undefined}{" "}
                        -{" "}
                        {contact.marketingConsentEndDate
                          ? formatLocaleDate(contact.marketingConsentEndDate)
                          : undefined}
                      </span>
                    ) : (
                      <span className={styles.marketingConsentContent}>
                        <AntIcon type="close-circle-two-tone" twoToneColor={cssVariables.colorRed} />
                        <Divider orientation="vertical" />
                        <i>{t("contact.enums.marketingConsent.NOT_GRANTED")}</i>
                      </span>
                    )}
                  </td>
                )}
              </tr>
            ))}

            {notMainContacts.length > 0 && showMainContactsDivider && (
              <tr>
                <td colSpan={4}>
                  <div style={{ borderBottom: "1px solid #ccc", margin: 2 }}></div>
                </td>
              </tr>
            )}
          </>
        )}

        {notMainContacts.map((contact) => (
          <tr key={`${contact.type}-${contact.value}`}>
            <td className={styles.type}>
              <span className={styles.typeContent}>
                <ContactTypeIcon showTooltip type={contact.type} />
              </span>
            </td>
            <td className={styles.value}>
              <CopyToClipboardView
                content={resolveContactValue(contact)}
                wrapper={
                  <Ellipsis
                    tooltip={resolveContactValue(contact)}
                    className={size === "small" ? styles.valueContentSmall : styles.valueContent}
                  >
                    {resolveContactAnchor(contact)}
                  </Ellipsis>
                }
              />
            </td>
            <td className={styles.description}>
              <Ellipsis className={size === "small" ? styles.descriptionContentSmall : styles.descriptionContent}>
                {contact.description}
              </Ellipsis>
            </td>
            {marketingConsentsAreApplicable && !isOtherContact(contact) && (
              <td className={styles.marketingConsent}>
                {contact.marketingConsent === MarketingConsent.GRANTED ? (
                  <span className={styles.marketingConsentContent}>
                    <AntIcon type="check-circle-two-tone" twoToneColor={cssVariables.colorGreen} />
                    <Divider orientation="vertical" />
                    {contact.marketingConsentStartDate
                      ? formatLocaleDate(contact.marketingConsentStartDate)
                      : undefined}{" "}
                    - {contact.marketingConsentEndDate ? formatLocaleDate(contact.marketingConsentEndDate) : undefined}
                  </span>
                ) : (
                  <span className={styles.marketingConsentContent}>
                    <AntIcon type="close-circle-two-tone" twoToneColor={cssVariables.colorRed} />
                    <Divider orientation="vertical" />
                    <i>{t("contact.enums.marketingConsent.NOT_GRANTED")}</i>
                  </span>
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  ) : (
    <span className="sub-header-info dashed">{t("contact.helpers.empty")}</span>
  );
};
